import { ApiError } from '@/api/errors'

/**
 * Fetch client for the Portal API.
 *
 * One request shape: JSON in, JSON out, a bearer token on every call. The
 * token comes from `auth/token.ts` per request rather than being captured
 * here, because the Auth0 SDK renews it in memory.
 */

export interface HttpClient {
  get<T>(path: string): Promise<T>
  post<T>(path: string, body: unknown): Promise<T>
  patch<T>(path: string, body: unknown): Promise<T>
  put<T>(path: string, body?: unknown): Promise<T>
  delete<T>(path: string): Promise<T>
}

export interface ClientOptions {
  baseUrl: string
  getToken: () => Promise<string | null>
  /** Injected in tests. */
  fetch?: typeof fetch
}

export function createClient({
  baseUrl,
  getToken,
  fetch: fetchImpl = fetch,
}: ClientOptions): HttpClient {
  const root = baseUrl.replace(/\/+$/, '')

  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await getToken()
    // Every route but /healthz requires a token (APP-51). Without one the
    // request can only 401, so fail here and say why.
    if (!token) throw new ApiError(401, 'Not signed in')

    const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
    if (body !== undefined) headers['Content-Type'] = 'application/json'

    let response: Response
    try {
      response = await fetchImpl(`${root}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      })
    } catch (cause) {
      // A CORS rejection lands here too: the browser hides it as a network error.
      throw new ApiError(0, `Could not reach the Portal API: ${String(cause)}`)
    }

    if (!response.ok) throw new ApiError(response.status, await errorMessage(response))
    if (response.status === 204) return undefined as T
    return (await response.json()) as T
  }

  return {
    get: (path) => request('GET', path),
    post: (path, body) => request('POST', path, body),
    patch: (path, body) => request('PATCH', path, body),
    put: (path, body) => request('PUT', path, body),
    delete: (path) => request('DELETE', path),
  }
}

/**
 * FastAPI's error body is `{detail: string}` for an HTTPException and
 * `{detail: [{loc, msg}, ...]}` for a 422 validation failure. Anything else
 * (a proxy's HTML page, an empty body) falls back to the status text.
 */
async function errorMessage(response: Response): Promise<string> {
  const fallback = `${response.status} ${response.statusText}`.trim()
  let body: unknown
  try {
    body = await response.json()
  } catch {
    return fallback
  }
  const detail = (body as { detail?: unknown } | null)?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    const messages = detail
      .map((d: { loc?: unknown[]; msg?: string }) => {
        // loc is ['body', 'primaryDomain'] -- the first segment is noise to a user.
        const field = Array.isArray(d.loc) ? d.loc.slice(1).join('.') : ''
        return field ? `${field}: ${d.msg}` : d.msg
      })
      .filter(Boolean)
    if (messages.length) return messages.join('; ')
  }
  return fallback
}
